/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

// DD-31: main.tsx mixes router/Sentry exports with the React root render call by
// design — separating them would require a significant restructure. The react-refresh
// warning is suppressed here at the file level; all other ESLint rules remain active.
/* eslint-disable react-refresh/only-export-components */

import React, { Suspense, lazy } from "react";
import * as Sentry from "@sentry/react";
import "./i18n";
import { createRoot } from "react-dom/client";
import {
  createBrowserRouter,
  RouterProvider,
  RouteObject,
  Navigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "./index.css";
import App from "./App";
import { URL_LANGUAGES } from "@/lib/constants";

// Lazy load ALL pages for optimal code splitting
const ProductPage = lazy(() => import("./pages/ProductPage"));
const HomePage = lazy(() => import("./pages/HomePage"));
const DownloadPage = lazy(() => import("./pages/DownloadPage"));
const TechnicalSupport = lazy(() => import("./pages/support/TechnicalSupport"));
const LostLicenseKey = lazy(() => import("./pages/support/LostLicenseKey"));

const ContactUs = lazy(() => import("./pages/company/ContactUs"));
const AboutDeskshare = lazy(() => import("./pages/company/AboutDeskshare"));
const MediaCoverage = lazy(() => import("./pages/MediaCoverage"));
const WhatsNew = lazy(() => import("./pages/WhatsNew"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Reviews = lazy(() => import("./pages/Reviews"));
const DetailedArticle = lazy(() => import("./pages/support/DetailedArticle"));
const MyPCInfo = lazy(() => import("./pages/MyPCInfo"));
const UserManual = lazy(() => import("./pages/support/UserManual"));
const CameraRequest = lazy(() => import("./pages/CameraRequest"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const PrivacyPolicyApp = lazy(() => import("./pages/PrivacyPolicyApp"));
const Faq = lazy(() => import("./pages/support/Faq"));
const PurchasePage = lazy(() => import("./pages/PurchasePage"));
const SecurityMonitorProPurchasePage = lazy(
  () => import("./pages/SecurityMonitorProPurchasePage"),
);
const UninstallFeedback = lazy(() => import("./pages/UninstallFeedback"));
const AuthLogin = lazy(() => import("./pages/auth/Login"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const AuthCallback = lazy(() => import("./pages/auth/AuthCallback"));
// Sign-in redirect entry: /:lang/sign-in forwards to merged /auth login.
// Rendered without marketing header/footer (standalone mechanism in App.tsx).
const SignIn = lazy(() => import("./pages/SignIn"));
// Bug 2 resolved: React Articles and VideoTutorialsAndManuals pages enabled.
// Nav links updated to use React Router paths; Traefik redirects the old
// ASP.NET URLs to the new routes — see HANDOFF.md for Sanjeev's action items.
const Articles = lazy(() => import("./pages/resources/Articles"));
const VideoTutorialsAndManuals = lazy(
  () => import("./pages/resources/VideoTutorialsAndManuals"),
);

import { AddLangToPath } from "./helper/AddLanguagePath";
import IPDetails from "./helper/IPDetails";
import { safeLocalStorage } from "./helper/safeLocalStorage";
import ErrorBoundary from "./ErrorBoundary";
import PageLoader from "./components/common/PageLoader";

// Helper to wrap lazy components with Suspense and ErrorBoundary
const withSuspense = (
  Component: React.LazyExoticComponent<React.ComponentType>,
) => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  </ErrorBoundary>
);

// Initialise language detection once at app startup.
// IP geolocation is fired here — not on every AddLangToPath() call —
// so the network request happens at most once per session.
// DD-12: URL_LANGUAGES imported from lib/constants — single source of truth
// DD-2: Export the promise so useLanguageInit can await it rather than firing
// a duplicate IPDetails() call while this one is still in-flight.
const initLanguage = async (): Promise<void> => {
  if (!safeLocalStorage.getItem("lang")) {
    try {
      const data = await IPDetails();
      // DD-23: country_code ('BR', 'MX', 'AT', 'CH' etc.) does not map to a language —
      // it maps to a country. Use the 'languages' field (e.g. 'pt-BR') returned by
      // /api/geo/ to extract the primary language code. Affects ~660M first-time visitors.
      let detectedLang = "en";
      if (data?.languages) {
        const primaryTag = data.languages.split(",")[0].trim(); // 'pt-BR'
        const langCode = primaryTag.split("-")[0].toLowerCase(); // 'pt'
        if ((URL_LANGUAGES as readonly string[]).includes(langCode)) {
          detectedLang = langCode;
        }
      } else if (data?.country_code) {
        // Fallback: country_code for simple cases where languages field is absent
        const ipLang = data.country_code.toLowerCase();
        if ((URL_LANGUAGES as readonly string[]).includes(ipLang)) {
          detectedLang = ipLang;
        }
      }
      safeLocalStorage.setItem("lang", detectedLang);
    } catch {
      // Network errors fetching IP data are expected (offline, firewall, etc.)
      safeLocalStorage.setItem("lang", "en");
    }
  }
};

export const languageInitPromise: Promise<void> = initLanguage();

const routerConfig: RouteObject[] = [
  {
    element: <App />,
    children: [
      {
        path: AddLangToPath("/"),
        element: withSuspense(HomePage),
      },
      {
        path: AddLangToPath("/contact-us"),
        element: withSuspense(ContactUs),
      },
      {
        path: AddLangToPath("/contact_us1.aspx"),
        element: withSuspense(ContactUs),
      },
      {
        path: AddLangToPath("/product-support"),
        element: withSuspense(TechnicalSupport),
      },
      {
        // DD-11: /priority-support appears on every product purchase page but had no route.
        // Redirect to /product-support until a dedicated page is built.
        path: AddLangToPath("/priority-support"),
        element: <Navigate to={AddLangToPath("/product-support")} replace />,
      },
      {
        path: AddLangToPath("/lost-license-key"),
        element: withSuspense(LostLicenseKey),
      },
      {
        path: AddLangToPath("/:productSlug"),
        element: withSuspense(ProductPage),
      },
      {
        path: AddLangToPath("/video-surveillance-software/purchase"),
        element: withSuspense(SecurityMonitorProPurchasePage),
      },
      {
        path: AddLangToPath("/:productSlug/purchase"),
        element: withSuspense(PurchasePage),
      },
      {
        path: AddLangToPath("/:productSlug/whatsnew/:whatsNewSlug"),
        element: withSuspense(WhatsNew),
      },
      {
        path: AddLangToPath("/:productSlug/reviews"),
        element: withSuspense(Reviews),
      },
      {
        path: AddLangToPath("/download"),
        element: withSuspense(DownloadPage),
      },
      // /articles route re-enabled — React SPA page (Bug 2)
      { path: AddLangToPath("/articles"), element: withSuspense(Articles) },
      {
        path: AddLangToPath("/:articleSlug/:detailedArticleSlug"),
        element: withSuspense(DetailedArticle),
      },
      // /tutorials-manuals route re-enabled — React SPA page (Bug 2)
      { path: AddLangToPath("/tutorials-manuals"), element: withSuspense(VideoTutorialsAndManuals) },
      {
        path: AddLangToPath("/help/:product_name_slug/:guideSlug"),
        element: withSuspense(UserManual),
      },
      {
        path: AddLangToPath("/faq/:product_name_slug/:guideSlug"),
        element: withSuspense(Faq),
      },
      {
        path: AddLangToPath("/about-deskshare"),
        element: withSuspense(AboutDeskshare),
      },
      {
        path: AddLangToPath("/media-coverage"),
        element: withSuspense(MediaCoverage),
      },
      {
        path: AddLangToPath("/mypcinfo"),
        element: withSuspense(MyPCInfo),
      },
      {
        path: AddLangToPath("/camera-request"),
        element: withSuspense(CameraRequest),
      },
      {
        path: AddLangToPath("/auth"),
        element: withSuspense(AuthLogin),
      },
      {
        path: AddLangToPath("/auth/forgot-password"),
        element: withSuspense(ForgotPassword),
      },
      {
        path: AddLangToPath("/auth/reset-password"),
        element: withSuspense(ResetPassword),
      },
      {
        path: AddLangToPath("/auth/callback"),
        element: withSuspense(AuthCallback),
      },
      {
        path: AddLangToPath("/privacy-policy"),
        element: withSuspense(PrivacyPolicy),
      },
      {
        path: AddLangToPath("/app-privacy-policy"),
        element: withSuspense(PrivacyPolicyApp),
      },
      {
        path: AddLangToPath("*"),
        element: withSuspense(NotFound),
      },
    ],
  },
  {
    path: AddLangToPath("/uninstall-feedback"),
    element: withSuspense(UninstallFeedback),
  },
  {
    // Legacy /sign-in entry — forwards to /auth on DeskShare marketing hosts.
    path: AddLangToPath("/sign-in"),
    element: withSuspense(SignIn),
  },
];

if (import.meta.env.MODE !== "development" && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN, // No hardcoded fallback — VITE_SENTRY_DSN must be set in Coolify env vars
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
      maskAllText:   true,  // mask all visible text before sending to Sentry
      blockAllMedia: true,  // block all media elements in session replays
    }),
    ],
    tracesSampleRate: 0.1, // DD-9: 10% sampling in production — 1.0 (100%) captures every transaction, exhausting quota
    replaysSessionSampleRate: 0.0, // DD-28: passive session recording disabled — undisclosed in Privacy Policy. Error-triggered replay kept below.
    replaysOnErrorSampleRate: 0.25, // DD-09: reduced from 1.0 — monitor replay quota
  });
}

// createBrowserRouter must be called after Sentry.init so tracing instruments routes correctly
const router = createBrowserRouter(routerConfig);

const initializeApp = (): void => {
  const rootElement = document.getElementById("root");
  if (rootElement) {
    const root = createRoot(rootElement);
    root.render(
      <React.StrictMode>
        <HelmetProvider>
          {/* Outer Suspense catches the RouterProvider's own lazy boundary during
              initial hydration. PageLoader keeps the UX consistent with per-page
              Suspense boundaries inside withSuspense(). */}
          <Suspense fallback={<PageLoader />}>
            <RouterProvider router={router} />
          </Suspense>
        </HelmetProvider>
      </React.StrictMode>,
    );
  } else {
    // Root element missing — the app cannot mount. Report to Sentry so the
    // deployment failure is visible; there is nothing to render to the user.
    Sentry.captureException(
      new Error("Root element #root not found — app failed to mount"),
      { tags: { context: "initializeApp" } },
    );
  }
};

// ES modules with type="module" are deferred by default.
// The DOM is guaranteed to be ready when this executes since:
// 1. Module scripts are deferred (execute after HTML parsing)
// 2. The script tag is placed at the end of body
// Direct invocation is safer than DOMContentLoaded which may have already fired.
initializeApp();
